import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { useMemo } from 'react';
import type {
  CallToAction,
  CategoryFeaturedCard as CategoryFeaturedCardSchema,
  DesktopMedia,
  MobileMedia,
} from '@amplience/content-types/typings/c-category-featured-card';
import { useMediaMatch } from '../../hooks/useMediaMatch';
import { hasHref } from '../../partials/link';
import type { MediaPartialProps } from '../../partials/media';
import { MediaPartial } from '../../partials/media';
import { media } from '../../stylings';
import { sendNavigationEvent } from '../../utils/gtm/events/navigation/sendNavigationEvent';
import { getLocalizedValue, transformTextToId } from '../../utils/transformers';
import {
  CardWrapper,
  Link,
  StyledCTA,
  StyledDesktopCard,
  StyledLinkPartial,
  StyledMobileCard,
  TexContainer,
  Title,
} from './CategoryFeaturedCard.styles';

export type CategoryFeaturedCardProps = CategoryFeaturedCardSchema & {
  extendWidth?: boolean;
  setMobileMenuState?: Dispatch<SetStateAction<boolean>>;
};

interface CartTitleProps {
  title?: string;
}

interface CTAProps {
  cta?: CallToAction;
}

type MobileCardProps = CartTitleProps &
  CTAProps & {
    mobile?: MobileMedia;
    handleClick: () => void;
  };

type DesktopCardProps = CartTitleProps &
  CTAProps & {
    desktop?: DesktopMedia;
    handleClick: () => void;
    extendWidth?: boolean;
  };

function CartTitle({ title }: CartTitleProps) {
  return <Title>{title}</Title>;
}

function CTA({ cta }: CTAProps) {
  if (cta) {
    const localizedText = getLocalizedValue(cta.text);
    const ctaId = transformTextToId(localizedText);
    return (
      <Link>
        {hasHref(cta.link) ? (
          <StyledLinkPartial id={ctaId} ariaLabel={localizedText} {...cta.link}>
            <StyledCTA>{localizedText}</StyledCTA>
          </StyledLinkPartial>
        ) : (
          <span>{localizedText}</span>
        )}
      </Link>
    );
  }
  return null;
}

function CardMedia({
  media: amplienceMedia,
  extendWidth,
}: {
  media?: DesktopMedia | MobileMedia;
  extendWidth?: boolean;
}) {
  const isDesktop = useMediaMatch(media.greaterThan('lg'));

  const dimensions = useMemo(() => {
    if (isDesktop) {
      return {
        ...amplienceMedia?.media,
        altText: amplienceMedia?.altText,
        videoQuality: amplienceMedia?.videoQuality,
        width: extendWidth ? 255 : 190,
        height: extendWidth ? 142 : 285,
      };
    }
    return {
      ...amplienceMedia?.media,
      altText: amplienceMedia?.altText,
      videoQuality: amplienceMedia?.videoQuality,
      width: 262,
      height: 148,
    };
  }, [amplienceMedia?.altText, amplienceMedia?.media, amplienceMedia?.videoQuality, extendWidth, isDesktop]);
  return (
    <CardWrapper>
      <MediaPartial {...(dimensions as MediaPartialProps)} />
    </CardWrapper>
  );
}

function MobileCard({ mobile, cta, title, handleClick }: MobileCardProps) {
  const localizedText = cta ? getLocalizedValue(cta.text) : '';

  return (
    <StyledMobileCard onClick={handleClick}>
      <CardMedia media={mobile} />
      <TexContainer>
        <Link>
          {cta && hasHref(cta.link) ? (
            <StyledLinkPartial ariaLabel={localizedText} {...cta.link}>
              <CartTitle title={title} />
              <StyledCTA>{localizedText}</StyledCTA>
            </StyledLinkPartial>
          ) : (
            <CartTitle title={title} />
          )}
        </Link>
      </TexContainer>
    </StyledMobileCard>
  );
}

function DesktopCard({ desktop, title, cta, handleClick, extendWidth = false }: DesktopCardProps) {
  return (
    <StyledDesktopCard onClick={handleClick} $extendWidth={extendWidth}>
      <CardMedia media={desktop} extendWidth={extendWidth} />
      <TexContainer>
        <CartTitle title={title} />
        <CTA cta={cta} />
      </TexContainer>
    </StyledDesktopCard>
  );
}

export function CategoryFeaturedCard({
  desktopMediaSlot,
  mobileMediaSlot,
  title,
  link: cta,
  extendWidth,
  setMobileMenuState = undefined,
}: CategoryFeaturedCardProps): ReactElement {
  const localizedText = getLocalizedValue(title);
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const handleClick = () => {
    sendNavigationEvent({
      component_header: localizedText,
      component_type: 'main nav - image link',
      link_text: getLocalizedValue(cta?.text),
    });
    if (setMobileMenuState) {
      setMobileMenuState(false);
    }
  };
  return isDesktop ? (
    <DesktopCard
      handleClick={handleClick}
      desktop={desktopMediaSlot}
      title={localizedText}
      cta={cta}
      extendWidth={extendWidth}
    />
  ) : (
    <MobileCard handleClick={handleClick} mobile={mobileMediaSlot} title={localizedText} cta={cta} />
  );
}

export default CategoryFeaturedCard;
